import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NextSeo } from "next-seo";
import { reverseStrength } from "domains/assets/utils";
import { useDebounce } from "domains/commons/hooks/useDebounce";
import FileManagerImage, {
  FileManagerImageProps,
} from "domains/file-manager/components/FileManagerImage";
import { FmImageCardActionsProps } from "domains/file-manager/components/FileManagerImage/Card/Actions";
import { FmImageListActionsProps } from "domains/file-manager/components/FileManagerImage/List/Actions";
import CopyPromptButton from "domains/file-manager/components/FileManagerImage/List/CopyPromptButton";
import MoreTooltip from "domains/file-manager/components/FileManagerImage/List/MoreTooltip";
import ReuseButton from "domains/file-manager/components/FileManagerImage/List/ReuseButton";
import { FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS } from "domains/file-manager/constants/AssetFilter";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import { useNewUserEmptyState } from "domains/file-manager/hooks/useNewUserEmptyState";
import InferenceGeneratorContent, {
  InferenceGeneratorTabsHandle,
} from "domains/inference/components/InferenceGenerator/Content";
import InferenceGeneratorSidebar from "domains/inference/components/InferenceGenerator/Sidebar";
import {
  SDXL_BASE_MODELS,
  SDXL_DEFAULT_BASE_MODEL,
} from "domains/inference/constants/baseModel";
import { INFERENCE_TYPE_LABELS } from "domains/inference/constants/inferenceTypes";
import { SCHEDULERS } from "domains/inference/constants/Schedulers";
import {
  InferenceCreateProvider,
  useInferenceCreateContext,
} from "domains/inference/contexts/InferenceProvider";
import {
  SectionsProvider,
  useSectionsContext,
} from "domains/inference/contexts/SectionsProvider";
import useImportParamsInference from "domains/inference/hooks/useImportParamsInference";
import {
  ApiInferenceControlNet,
  ApiInferenceControlNetImg2Img,
  ApiInferenceControlNetIpAdapter,
  ApiInferenceImg2Img,
  ApiInferenceImg2ImgIpAdapter,
  ApiInferenceTxt2Img,
  ApiInferenceTxt2ImgIpAdapter,
} from "domains/inference/interfaces/ApiTypes";
import { getIsControlEndAvailable } from "domains/inference/utils/controlEnd";
import {
  JobSessionProvider,
  useJobSessionContext,
} from "domains/jobs/contexts/JobSessionProviderV2";
import { getAssetNumberFromJob } from "domains/jobs/utils";
import {
  getIsModelFlux,
  getIsModelFluxDev,
  getIsModelFluxPro,
  getIsModelFluxPro1_1,
  getIsModelFluxPro1_1Ultra,
  getIsModelFluxSchnell,
  getIsModelGptImage,
  getIsModelSdxl,
} from "domains/models/utils";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import {
  GeneratorContainer,
  GeneratorContent,
  GeneratorSidebar,
} from "domains/ui/components/GeneratorLayout";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import { removeCuFromType } from "infra/api/ExcludeCU";
import {
  GetAssetsByAssetIdApiResponse,
  useGetModelsByModelIdQuery,
  useLazyGetModelsInferencesByModelIdAndInferenceIdQuery,
} from "infra/api/generated/api";
import {
  useGetInferenceCostQuery,
  usePostInferenceMutation,
} from "infra/store/apiSlice";
import _ from "lodash";

import { skipToken } from "@reduxjs/toolkit/dist/query";

export default function ImageNewPage() {
  return (
    <>
      <NextSeo title="Generate Images" />

      <SectionsProvider
        defaultCollapsedSections={[
          "concepts",
          "referenceImage",
          "negativePrompt",
        ]}
      >
        <InferenceCreateProvider>
          <JobSessionProvider
            forcedFilterTypeOptions={[
              ...FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS,
            ]}
          >
            <ImageNew />
          </JobSessionProvider>
        </InferenceCreateProvider>
      </SectionsProvider>
    </>
  );
}

// ------------------------------------

function ImageNew() {
  const { errorToast, successToast, infoToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { selectedProject, isReader } = useTeamContext();
  const { openSection } = useSectionsContext();
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const {
    nbImages,
    samplingSteps,
    width,
    height,
    referenceImages,
    isReferencesRatioValid,
    guidance,
    seed,
    model,
    baseModelId,
    prompt,
    negativePrompt,
    scheduler,
    referenceControlNet,
    isReferenceModeMappingEnabled,
    referenceInfluences,
    referenceModeMapAsset,
    concepts,
    inferenceType,
    ipAdapterType,
    isFluxPro1_1,
    isModelWithBase,
    setCuCost,
    setIsCuLoading,
    setGuidance,
    setScheduler,
    setSamplingSteps,
    setSeed,
    setWidth,
    setHeight,
    setBaseModelId,
    setPrompt,
    setNegativePrompt,
    setModelFromModelId,
    setReferenceFromInference,
    setReferenceImage,
    setNbImages,
    canHaveReference,
    controlEnd,
    isReferenceModeMapLoading,
  } = useInferenceCreateContext();
  const [getInferenceById] =
    useLazyGetModelsInferencesByModelIdAndInferenceIdQuery();
  const { showLimitModal } = usePlanContext();
  const [generateImageTrigger] = usePostInferenceMutation();
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<InferenceGeneratorTabsHandle>(null);

  const isMissingMandatoryPrompt = useMemo(() => {
    const modelNeedsPrompt =
      getIsModelFluxPro(model) ||
      getIsModelFluxPro1_1(model) ||
      getIsModelFluxPro1_1Ultra(model) ||
      getIsModelGptImage(model);
    const hasImage =
      referenceImages.reference ||
      referenceImages.control ||
      referenceImages.ipAdapter;
    return modelNeedsPrompt && !hasImage && !prompt;
  }, [
    model,
    referenceImages.control,
    referenceImages.ipAdapter,
    referenceImages.reference,
    prompt,
  ]);
  const generationParams = useMemo(() => {
    if (!model?.id) return undefined;

    const globalParameters = {
      modelId: model.id,
      seed: nbImages === 1 && seed ? seed : undefined,
      prompt,
      negativePrompt: negativePrompt ?? undefined,
      guidance,
      numInferenceSteps: samplingSteps,
      numSamples: nbImages,
      width: width,
      height: height,
      scheduler,
      concepts,
      baseModelId:
        getIsModelSdxl(model) &&
        model.id !== "stable-diffusion-xl-base-1.0" &&
        baseModelId !== SDXL_DEFAULT_BASE_MODEL.id
          ? baseModelId
          : undefined,
    };

    if (referenceImages.reference && inferenceType === "txt2img_ip_adapter") {
      const body: ApiInferenceTxt2ImgIpAdapter = {
        inferenceType: "txt2img-ip-adapter",
        projectId: selectedProject.id,
        body: {
          ...globalParameters,
          ...((getIsModelFluxDev(model) || getIsModelFluxSchnell(model)) &&
          referenceImages.reference.id
            ? {
                ipAdapterImageIds: [referenceImages.reference.id],
                ipAdapterScales: [referenceInfluences.reference],
              }
            : {
                ipAdapterImageId: referenceImages.reference.id,
                ipAdapterScale: referenceInfluences.reference,
              }),
          ipAdapterType,
        },
      };
      return body;
    } else if (
      referenceImages.ipAdapter &&
      referenceImages.reference &&
      inferenceType === "img2img_ip_adapter"
    ) {
      const body: ApiInferenceImg2ImgIpAdapter = {
        inferenceType: "img2img-ip-adapter",
        projectId: selectedProject.id,
        body: {
          ...globalParameters,
          ...((getIsModelFluxDev(model) || getIsModelFluxSchnell(model)) &&
          referenceImages.ipAdapter.id
            ? {
                ipAdapterImageIds: [referenceImages.ipAdapter.id],
                ipAdapterScales: [referenceInfluences.ipAdapter],
              }
            : {
                ipAdapterImageId: referenceImages.ipAdapter.id,
                ipAdapterScale: referenceInfluences.ipAdapter,
              }),
          ipAdapterType,
          strength: reverseStrength(referenceInfluences.reference),
          imageId: referenceImages.reference.id,
        },
      };
      return body;
    } else if (
      referenceImages.ipAdapter &&
      referenceImages.reference &&
      inferenceType === "controlnet_ip_adapter"
    ) {
      const body: ApiInferenceControlNetIpAdapter = {
        inferenceType: "controlnet-ip-adapter",
        projectId: selectedProject.id,
        body: {
          ...globalParameters,
          ipAdapterImageId: referenceImages.ipAdapter.id,
          ipAdapterScale: referenceInfluences.ipAdapter,
          ipAdapterType,

          imageId: referenceModeMapAsset
            ? referenceModeMapAsset.id
            : referenceImages.reference.id,

          modality: `${referenceControlNet}:${referenceInfluences.reference}`,
          disableModalityDetection: referenceModeMapAsset
            ? true
            : !isReferenceModeMappingEnabled,
          controlEnd: getIsControlEndAvailable(model) ? controlEnd : undefined,
        },
      };
      return body;
    } else if (
      referenceImages.reference &&
      referenceImages.control &&
      inferenceType === "controlnet_img2img"
    ) {
      const body: ApiInferenceControlNetImg2Img = {
        inferenceType: "controlnet-img2img",
        projectId: selectedProject.id,
        body: {
          ...globalParameters,
          strength: reverseStrength(referenceInfluences.reference),
          modality: `${referenceControlNet}:${referenceInfluences.control}`,
          disableModalityDetection: referenceModeMapAsset
            ? true
            : !isReferenceModeMappingEnabled,
          controlImageId: referenceModeMapAsset
            ? referenceModeMapAsset.id
            : referenceImages.control.id,
          imageId: referenceImages.reference.id,
          controlEnd: getIsControlEndAvailable(model) ? controlEnd : undefined,
        },
      };
      return body;
    } else if (referenceImages.reference && inferenceType === "controlnet") {
      const body: ApiInferenceControlNet = {
        inferenceType: "controlnet",
        projectId: selectedProject.id,
        body: {
          ...globalParameters,
          modality: `${referenceControlNet}:${referenceInfluences.reference}`,
          disableModalityDetection: referenceModeMapAsset
            ? true
            : !isReferenceModeMappingEnabled,
          imageId: referenceModeMapAsset
            ? referenceModeMapAsset.id
            : referenceImages.reference.id,
          controlEnd: getIsControlEndAvailable(model) ? controlEnd : undefined,
        },
      };
      return body;
    } else if (referenceImages.reference && inferenceType !== "txt2img") {
      const body: ApiInferenceImg2Img = {
        inferenceType: "img2img",
        projectId: selectedProject.id,
        body: {
          ...globalParameters,
          strength: reverseStrength(referenceInfluences.reference),
          imageId: referenceImages.reference.id,
        },
      };
      return body;
    } else {
      const body: ApiInferenceTxt2Img = {
        inferenceType: "txt2img",
        projectId: selectedProject.id,
        body: {
          ...globalParameters,
        },
      };
      return body;
    }
  }, [
    model,
    selectedProject.id,
    nbImages,
    seed,
    negativePrompt,
    guidance,
    samplingSteps,
    width,
    prompt,
    height,
    scheduler,
    concepts,
    referenceImages.reference,
    referenceImages.ipAdapter,
    referenceImages.control,
    inferenceType,
    referenceInfluences.reference,
    referenceInfluences.ipAdapter,
    referenceInfluences.control,
    referenceModeMapAsset,
    referenceControlNet,
    isReferenceModeMappingEnabled,
    ipAdapterType,
    baseModelId,
    controlEnd,
  ]);
  const debouncedGenerationParams = useDebounce(generationParams, 1_000, {
    resetBeforeDebounce: true,
    preventFor: (val) => !val,
  });

  const isDisabled = useMemo(() => {
    return (
      isGeneratingImage ||
      !generationParams ||
      isMissingMandatoryPrompt ||
      isReferenceModeMapLoading ||
      !isReferencesRatioValid
    );
  }, [
    isGeneratingImage,
    generationParams,
    isMissingMandatoryPrompt,
    isReferenceModeMapLoading,
    isReferencesRatioValid,
  ]);

  const { data: costData, isFetching: isCuLoading } = useGetInferenceCostQuery(
    isDisabled || !debouncedGenerationParams || isReader
      ? skipToken
      : debouncedGenerationParams
  );

  const debouncedOutput = useDebounce({ nbImages, width, height }, 100);
  const placeholders = useGetFileImagePlaceholders({
    length: debouncedOutput.nbImages,
    dimensions: {
      width: debouncedOutput.width,
      height: debouncedOutput.height,
    },
  });

  // ----------------------------------

  const {
    NewUserEmptyStateComponent,
    fmHeaderStyleProps,
    isLoading: isNewUserEmptyStateLoading,
    setHasGeneratedOnce,
  } = useNewUserEmptyState({
    modelId: model?.id,
  });

  const generateImage = useCallback(async () => {
    try {
      if (isDisabled || !generationParams) {
        return;
      }

      setIsGeneratingImage(true);

      tabsRef.current?.reset();

      const { job } = await generateImageTrigger(generationParams)
        .unwrap()
        .then(removeCuFromType);
      setHasGeneratedOnce(true);
      addJob(job);

      Track(AnalyticsEvents.Inference.CreatedInference, {
        ...generationParams.body,
      });
      successToast({
        title: "Images are being generated",
      });
    } catch (error: any) {
      handleApiError(error, "Error generating images", {
        quota: () => {
          if (_.get(error, "data.details.remainingSeconds")) {
            showLimitModal("planCooldown", {
              timeout: error.data.details.remainingSeconds,
              type: "generation",
            });
          } else if (_.get(error, "data.name") === "RateLimitError") {
            showLimitModal("planImageGenerationsRateLimit");
          } else if (
            _.get(error, "data.details.actionName") === "parallel-inferences"
          ) {
            showLimitModal("parallelInferences");
          } else if (
            _.get(error, "data.reason").includes(
              "You have reached your plan's limit."
            )
          ) {
            showLimitModal("notEnoughCreativeUnits");
          } else {
            errorToast({
              title: "Error generating images",
              description: _.get(error, "data.reason"),
            });
          }
        },
        file_size: () => {
          errorToast({
            title: "Error generating images",
            description: "The reference image file size is too big.",
          });
        },
      });
    } finally {
      setIsGeneratingImage(false);
    }
  }, [
    generationParams,
    generateImageTrigger,
    setHasGeneratedOnce,
    addJob,
    successToast,
    handleApiError,
    showLimitModal,
    errorToast,
    isDisabled,
  ]);

  // ----------------------------------

  const handleMoreClick = useCallback(
    async (key: string, value: any) => {
      try {
        if (key === "guidance") {
          setGuidance(value);
          openSection("settings");
        } else if (key === "scheduler") {
          setScheduler(value ?? undefined);
          openSection("settings");
        } else if (key === "steps") {
          setSamplingSteps(value);
          openSection("settings");
        } else if (key === "seed") {
          setSeed(value);
          setNbImages(1);
          openSection("settings");
        } else if (key === "dimensions") {
          setWidth(parseInt(value.split("x")[0]));
          setHeight(parseInt(value.split("x")[1]));
          openSection("settings");
        } else if (key === "baseModelId") {
          setBaseModelId(value);
          openSection("settings");
        } else if (key === "modelId") {
          await setModelFromModelId(value.modelId, value.concepts);
        } else if (key === "reference") {
          const inferenceData = await getInferenceById({
            modelId: value.modelId,
            inferenceId: value.inferenceId,
            projectId: selectedProject.id,
          }).unwrap();
          if (!inferenceData?.inference) {
            throw new Error("unknown inference");
          }

          setReferenceFromInference(inferenceData.inference);
        }

        infoToast({
          title: "Settings updated",
        });
      } catch (err) {
        errorToast({
          title: "Failed to copy to settings",
        });
      }
    },
    [
      getInferenceById,
      infoToast,
      errorToast,
      openSection,
      selectedProject.id,
      setBaseModelId,
      setGuidance,
      setHeight,
      setModelFromModelId,
      setReferenceFromInference,
      setSamplingSteps,
      setScheduler,
      setSeed,
      setNbImages,
      setWidth,
    ]
  );

  const handleCopyPromptClick = useCallback<
    ListActionsProps["onCopyPromptClick"]
  >(
    ({ prompt, negativePrompt }) => {
      openSection("prompt");
      setPrompt(prompt);

      if (negativePrompt) {
        openSection("negativePrompt");
        setNegativePrompt(negativePrompt);
      }

      infoToast({
        title: "Settings updated",
      });
    },
    [openSection, setPrompt, setNegativePrompt, infoToast]
  );

  const handleRefineClick = useCallback<
    CardActionsCustomProps["onRefineClick"]
  >(
    async (asset) => {
      await setReferenceImage("reference", asset);

      infoToast({
        title: "Settings updated",
      });
    },
    [setReferenceImage, infoToast]
  );

  const handleReuseModelClick = useCallback<
    CardActionsCustomProps["onReuseModelClick"]
  >(
    async (modelId) => {
      await setModelFromModelId(modelId);

      infoToast({
        title: "Settings updated",
      });
    },
    [setModelFromModelId, infoToast]
  );

  const handleReuseSeedClick = useCallback<
    CardActionsCustomProps["onReuseSeedClick"]
  >(
    (seed) => {
      setSeed(seed);
      setNbImages(1);
      openSection("settings");

      infoToast({
        title: "Settings updated",
      });
    },
    [setSeed, setNbImages, openSection, infoToast]
  );

  // ----------------------------------

  useEffect(() => {
    setCuCost(costData?.creativeUnitsCost);
    setIsCuLoading(
      isCuLoading || !_.isEqual(generationParams, debouncedGenerationParams)
    );
  }, [
    costData,
    debouncedGenerationParams,
    generationParams,
    isCuLoading,
    setCuCost,
    setIsCuLoading,
  ]);

  useImportParamsInference({ onValueChange: openSection });

  // ----------------------------------

  const listActionsProps = useMemo(
    () => ({
      isFluxPro1_1,
      isModelWithBase,
      onCopyPromptClick: handleCopyPromptClick,
      onMoreClick: handleMoreClick,
    }),
    [handleCopyPromptClick, handleMoreClick, isFluxPro1_1, isModelWithBase]
  );

  const cardActionsProps = useMemo(
    () => ({
      canHaveReference,
      onRefineClick: handleRefineClick,
      onReuseModelClick: handleReuseModelClick,
      onReusePromptClick: handleCopyPromptClick,
      onReuseSeedClick: handleReuseSeedClick,
    }),
    [
      canHaveReference,
      handleRefineClick,
      handleReuseModelClick,
      handleCopyPromptClick,
      handleReuseSeedClick,
    ]
  );

  // ----------------------------------

  const headerStyleProps = useMemo<FileManagerImageProps["styleProps"]>(
    () => ({
      header: {
        top: "calc(57px + var(--chakra-space-1))",
        pt: 1,
        ...fmHeaderStyleProps,
      },
    }),
    [fmHeaderStyleProps]
  );

  return (
    <GeneratorContainer onSubmit={generateImage}>
      <GeneratorSidebar>
        <InferenceGeneratorSidebar
          onInferenceRequest={generateImage}
          isLoading={isGeneratingImage}
          isDisabled={isDisabled}
          generateButtonDisabledTooltip={
            (isMissingMandatoryPrompt &&
              "A prompt is required for this model and generation mode.") ||
            (!isReferencesRatioValid &&
              "Aspect ratios between reference images must be within 20% of each other.") ||
            undefined
          }
        />
      </GeneratorSidebar>

      <GeneratorContent scrollRef={scrollRef}>
        <InferenceGeneratorContent
          type="image"
          CardActionsComponent={CardActions}
          cardActionsProps={cardActionsProps}
          suggestArgs={prompt || model ? { prompt, model } : undefined}
          scrollRef={scrollRef}
          tabsRef={tabsRef}
        >
          <FileManagerImage
            scrollRef={scrollRef}
            files={
              !files?.length &&
              !jobs?.length &&
              !isLoading &&
              !NewUserEmptyStateComponent &&
              !isNewUserEmptyStateLoading
                ? placeholders
                : files
            }
            jobs={jobs}
            isSelectable
            isLoading={isLoading || isNewUserEmptyStateLoading}
            ListActionsComponent={ListActions}
            listActionsProps={listActionsProps}
            CardActionsComponent={CardActions}
            cardActionsProps={cardActionsProps}
            EmptyStateComponent={NewUserEmptyStateComponent}
            styleProps={headerStyleProps}
            {...fileManagerProps}
          />
        </InferenceGeneratorContent>
      </GeneratorContent>
    </GeneratorContainer>
  );
}

// ------------------------------------

export type CardActionsCustomProps = {
  canHaveReference: boolean;
  onRefineClick: (asset: GetAssetsByAssetIdApiResponse["asset"]) => void;
  onReuseModelClick: (modelId: string) => void;
  onReusePromptClick: (data: {
    prompt: string;
    negativePrompt?: string;
  }) => void;
  onReuseSeedClick: (seed: string) => void;
};

export type CardActionsProps = FmImageCardActionsProps<CardActionsCustomProps>;

function CardActions({
  asset,
  top,
  bottom,
  canHaveReference,
  onRefineClick,
  onReuseModelClick,
  onReusePromptClick,
  onReuseSeedClick,
}: CardActionsProps) {
  const modelId = asset?.metadata.modelId;
  const prompt = asset?.metadata.prompt;
  const negativePrompt = asset?.metadata.negativePrompt;
  const seed = asset?.metadata.seed;
  const canReuseImage =
    !["vectorization"].includes(asset?.metadata.type ?? "") && canHaveReference;

  const handleModelClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedModel);
      if (modelId) {
        onReuseModelClick(modelId);
      }
    },
    [onReuseModelClick, modelId]
  );

  const handleImageClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedImage);
      if (asset) {
        onRefineClick(asset);
      }
    },
    [onRefineClick, asset]
  );

  const handlePromptClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedPrompt);
      if (prompt) {
        onReusePromptClick({ prompt, negativePrompt });
      }
    },
    [onReusePromptClick, prompt, negativePrompt]
  );

  const handleSeedClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedSeed);
      if (seed) {
        onReuseSeedClick(seed);
      }
    },
    [onReuseSeedClick, seed]
  );

  if (!modelId && !canReuseImage && !prompt && !seed) {
    return null;
  }

  return (
    <>
      {top}

      {modelId && (
        <MenuItem
          w="170px"
          iconId="Nav/Model/Outline"
          iconH="12px"
          onClick={handleModelClick}
          text="Model"
        />
      )}

      {canReuseImage && (
        <MenuItem
          w="170px"
          iconId="Nav/Image/Outline"
          iconH="12px"
          onClick={handleImageClick}
          text="Image"
        />
      )}

      {!!prompt && (
        <MenuItem
          w="170px"
          iconId="Ui/Prompt"
          iconH="12px"
          onClick={handlePromptClick}
          text="Prompt"
        />
      )}

      {!!seed && (
        <MenuItem
          w="170px"
          iconId="Ui/Key"
          iconH="14px"
          onClick={handleSeedClick}
          text="Seed"
        />
      )}

      {bottom}
    </>
  );
}

// ------------------------------------

export type ListActionsCustomProps = {
  isFluxPro1_1?: boolean;
  isModelWithBase?: boolean;
  onCopyPromptClick: (arg: { prompt: string; negativePrompt?: string }) => void;
  onMoreClick: (key: string, value: any) => void;
};

export type ListActionsProps = FmImageListActionsProps<ListActionsCustomProps>;

function ListActions({
  job,
  firstAsset,
  isFluxPro1_1 = false,
  isModelWithBase = false,
  onCopyPromptClick,
  onMoreClick,
}: ListActionsProps) {
  const { selectedProject, isReader } = useTeamContext();
  const [isMoreOpen, setIsMoreOpen] = useState<boolean>(false);

  const jobInput = job?.metadata.input as any;
  const isActionDisplayed =
    ["flux", "inference"].includes(job?.jobType ?? "") && !isReader;

  const modelId = jobInput?.modelId;
  const concepts = jobInput?.concepts;
  const { data: modelData, isLoading: isModelLoading } =
    useGetModelsByModelIdQuery(
      isMoreOpen && isActionDisplayed && modelId
        ? { projectId: selectedProject.id, modelId }
        : skipToken
    );

  const guidance = jobInput?.guidance;
  const scheduler =
    (jobInput?.scheduler &&
      SCHEDULERS[jobInput.scheduler as keyof typeof SCHEDULERS]) ||
    (modelData?.model && !getIsModelFlux(modelData?.model) && "default") ||
    undefined;
  const dimensions =
    jobInput?.width && jobInput?.height
      ? `${jobInput?.width} x ${jobInput?.height}`
      : undefined;
  const steps = jobInput?.numInferenceSteps;
  const seed =
    job && getAssetNumberFromJob(job) === 1
      ? jobInput?.seed || firstAsset?.metadata.seed
      : undefined;
  const baseModelId = jobInput?.baseModelId;
  const baseModel = useMemo(
    () =>
      baseModelId
        ? SDXL_BASE_MODELS.find((baseModel) => baseModel.id === baseModelId)
        : undefined,
    [baseModelId]
  );

  const reference =
    ["flux", "inference"].includes(job?.jobType ?? "") &&
    jobInput.type !== "txt2img" &&
    _.compact([jobInput.type, jobInput.ipAdapterType]).join("_");
  const inferenceId = jobInput?.inferenceId;

  const moreOptions = useMemo(() => {
    if (!isActionDisplayed) {
      return [];
    }
    return _.compact([
      !!modelId && {
        id: "model",
        label: "Model",
        value: modelData?.model.name,
        isLoading: !modelData || isModelLoading,
        onClick:
          !modelData || isModelLoading
            ? undefined
            : () => onMoreClick("modelId", { modelId, concepts }),
      },
      !!reference &&
        !!modelId &&
        !!inferenceId && {
          id: "reference",
          label: "Reference",
          value:
            INFERENCE_TYPE_LABELS[
              reference as keyof typeof INFERENCE_TYPE_LABELS
            ] ?? "Yes",
          onClick: () => onMoreClick("reference", { modelId, inferenceId }),
        },
      !isFluxPro1_1 &&
        steps !== undefined && {
          id: "steps",
          label: "Sampling Steps",
          value: steps,
          onClick: () => onMoreClick("steps", steps),
        },
      !isFluxPro1_1 &&
        guidance !== undefined && {
          id: "guidance",
          label: "Guidance",
          value: guidance,
          onClick: () => onMoreClick("guidance", guidance),
        },
      dimensions !== undefined && {
        id: "dimensions",
        label: "Dimensions",
        value: dimensions,
        onClick: () => onMoreClick("dimensions", dimensions),
      },
      isModelWithBase &&
        baseModelId && {
          id: "baseModelId",
          label: "Base Model",
          value: baseModel?.name ?? "-",
          onClick: () => onMoreClick("baseModelId", baseModelId),
        },
      {
        id: "scheduler",
        label: "Scheduler",
        isLoading: !modelData || isModelLoading,
        value: !scheduler
          ? "-"
          : scheduler === "default"
          ? "Default"
          : scheduler,
        onClick: scheduler
          ? () => onMoreClick("scheduler", jobInput.scheduler)
          : undefined,
      },
      seed !== undefined && {
        id: "seed",
        label: "Seed",
        value: seed,
        onClick: () => onMoreClick("seed", seed),
      },
    ]);
  }, [
    baseModel?.name,
    baseModelId,
    concepts,
    dimensions,
    guidance,
    inferenceId,
    isActionDisplayed,
    isFluxPro1_1,
    isModelLoading,
    isModelWithBase,
    jobInput.scheduler,
    modelData,
    modelId,
    onMoreClick,
    reference,
    scheduler,
    seed,
    steps,
  ]);

  // ----------------------------------

  const handleMoreOpen = useCallback(() => {
    setIsMoreOpen(true);
  }, [setIsMoreOpen]);

  const handleMoreClose = useCallback(() => {
    setIsMoreOpen(false);
  }, [setIsMoreOpen]);

  // ----------------------------------

  if (isReader) {
    return null;
  }

  return (
    <>
      {isActionDisplayed && <ReuseButton job={job} firstAsset={firstAsset} />}
      <CopyPromptButton
        job={job}
        firstAsset={firstAsset}
        onClick={onCopyPromptClick}
      />
      {isActionDisplayed && (
        <MoreTooltip
          options={moreOptions}
          onOpen={handleMoreOpen}
          onClose={handleMoreClose}
        />
      )}
    </>
  );
}
